import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AiConsentOpsRecord, AiConsentSenderStanding, AiConsentState } from './ai-consent.types';

export interface BusinessConsentResponse {
  readonly ai_consent: AiConsentState | null;
  /** Who gave a standing consent — for "given [date] by [name]" (DOC-72 §2). */
  readonly given_by_name?: string;
}

/**
 * The business's AI-assessment consent (FLOW-297) — the client acts and the
 * Ops record. Ops has no give or restore call here, because there is none to
 * make (`§A6.7.4.2`).
 */
@Injectable({ providedIn: 'root' })
export class BusinessAiConsentService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/api/entities`;

  getState(entityShortId: string): Observable<BusinessConsentResponse> {
    return this.http.get<BusinessConsentResponse>(`${this.base}/${entityShortId}/ai-consent/`);
  }

  give(entityShortId: string): Observable<BusinessConsentResponse> {
    return this.http.post<BusinessConsentResponse>(`${this.base}/${entityShortId}/ai-consent/`, {
      ai_processing_consent: true,
    });
  }

  withdraw(entityShortId: string): Observable<BusinessConsentResponse> {
    return this.http.post<BusinessConsentResponse>(
      `${this.base}/${entityShortId}/ai-consent/withdraw/`,
      {},
    );
  }

  getOpsRecord(entityShortId: string): Observable<AiConsentOpsRecord> {
    return this.http.get<AiConsentOpsRecord>(`${this.base}/ops/${entityShortId}/ai-consent/`);
  }

  recordEmailedWithdrawal(
    entityShortId: string,
    body: {
      arrived_at: string;
      email_text: string;
      sender?: string;
      sender_standing?: AiConsentSenderStanding | '';
      standing_basis?: string;
    },
  ): Observable<AiConsentOpsRecord> {
    return this.http.post<AiConsentOpsRecord>(
      `${this.base}/ops/${entityShortId}/ai-consent/withdraw-received/`,
      body,
    );
  }

  recordDirectorCheck(
    entityShortId: string,
    body: {
      cipc_disclosure: string;
      cipc_disclosure_date: string;
      result: 'SHOWN' | 'NOT_SHOWN' | 'NOT_ESTABLISHED';
      basis?: string;
    },
  ): Observable<AiConsentOpsRecord> {
    return this.http.post<AiConsentOpsRecord>(
      `${this.base}/ops/${entityShortId}/ai-consent/director-check/`,
      body,
    );
  }
}
